export const STATUS_TYPES = ['Active', 'OnHold', 'Canceled', 'Closed'];
export type StatusType = (typeof STATUS_TYPES)[number];

export const CANDIDATE_JOB_STATUS_TYPES = [
  'Initial message sent',
  'Talent acquisition screening',
  'In progress talent acquisition',
  'Not selected',
  'In progress client',
  'Rejected proposal',
  'Hired',
];

export enum CandidateJobStatus {
  All = 'all',
  InitialMessageSent = 'InitialMessageSent',
  TalentAcquisitionScreening = 'TalentAcquisitionScreening',
  InProgressTalentAcquisition = 'InProgressTalentAcquisition',
  NotSelected = 'NotSelected',
  InProgressClient = 'InProgressClient',
  RejectedProposal = 'RejectedProposal',
  Hired = 'Hired',
}

export enum ROLES {
  admin = 'App.Admin',
  recruiter = 'App.Recruiter',
  internal = 'App.Internal',
  operations = 'App.Operations',
}

export enum JobStatus {
  Active = 'Active',
  OnHold = 'OnHold',
  Canceled = 'Canceled',
  Closed = 'Closed',
}

export type CandidateJobStatusType = (typeof CANDIDATE_JOB_STATUS_TYPES)[number];

export enum ReferralSourceOptions {
  LinkedInRecruiter = 1,
  Referral = 2,
  Portal = 3,
  Other = 4,
  LinkedInJobs = 5,
}

export const REFERRAL_ENUM: Record<string, number> = {
  LinkedInRecruiter: 1,
  Referral: 2,
  Portal: 3,
  Other: 4,
  LinkedInJobs: 5,
};

export const candidateJobStatusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Initial message sent', value: 'InitialMessageSent' },
  { label: 'Talent Acquisition screening', value: 'TalentAcquisitionScreening' },
  { label: 'In progress (Talent Acquisition)', value: 'InProgressTalentAcquisition' },
  { label: 'Not selected', value: 'NotSelected' },
  { label: 'In progress (Client)', value: 'InProgressClient' },
  { label: 'Stand-by (Client)', value: 'StandByClient' },
  { label: 'Pre selected', value: 'PreSelected' },
  { label: 'Rejected the proposal', value: 'RejectedProposal' },
  { label: 'Hired', value: 'Hired' },
];

export const BlockedFileExtensions: string[] = [
  'exe',
  'bat',
  'cmd',
  'com',
  'msi',
  'scr',
  'pif',
  'rar',
  'zip',
];

export const MAX_FILE_SIZE_MB = 10;
export const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

export const initialMessageSentSubStatusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Pending response', value: 'PendingResponse' },
  { label: 'Rejected message', value: 'RejectedMessage' },
  { label: 'Pending contact (process on hold)', value: 'PendingContact' },
];

export const InProgressTalentAcquisitionSubStatusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Talent Acquisition interview', value: 'TalentAcquisitionInterview' },
  { label: 'English test', value: 'EnglishTest' },
  { label: 'Technical Test', value: 'TechnicalTest' },
];

export const NotSelectedSubStatusOptions = [
  { label: 'All', value: 'all' },
  { label: 'English level', value: 'EnglishLevel' },
  { label: 'Technical requirements', value: 'TechnicalRequirements' },
  { label: 'Seniority', value: 'Seniority' },
  { label: 'Could not contact', value: 'CouldNotContact' },
  { label: 'Attitude', value: 'Attitude' },
  { label: 'By internal client', value: 'ByInternalClient' },
  { label: 'By external client', value: 'ByExternalClient' },
  { label: 'Salary expectations', value: 'SalaryExpectations' },
  { label: 'Other', value: 'Other' },
];

export const InProgressClientSubStatusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Internal', value: 'Internal' },
  { label: 'External', value: 'External' },
];

export const RejectedProposalSubStatusOptions = [
  { label: 'All', value: 'all' },
  { label: 'Decided Not To Continue', value: 'DecidedNotToContinue' },
  { label: 'Rejected Job Offer', value: 'RejectedJobOffer' },
];

export const SENIORITY_TYPES = ['Advanced', 'Advanced++', 'Sr.', 'Jr.'];
export type SeniorityType = (typeof SENIORITY_TYPES)[number];

export const getOptions = (options: string[]): { label: string; value: string }[] => {
  return options.map((type) => {
    return {
      label: type,
      value: type,
    };
  });
};

export const getOptionsFromDictionary = (
  options: { id: number; name: string }[],
): { label: string; value: string }[] => {
  return options.map((item) => ({
    label: item.name,
    value: item.id.toString(),
  }));
};

export const getOptionsFromEnum = (
  enumObj: Record<string, number>,
): { label: string; value: string }[] => {
  return Object.entries(enumObj).map(([label, id]) => ({
    label,
    value: id.toString(),
  }));
};

export const CURRENCY_TYPES = ['USD', 'UYU'];
export type CurrencyType = (typeof CURRENCY_TYPES)[number];

export const PRIORITY_TYPES = ['High', 'Medium', 'Low'];
export type PriorityType = (typeof PRIORITY_TYPES)[number];

export const CHARACTERISTICS_TYPES = [
  'Market Survey',
  'Active Process',
  'Selection Bench Process',
  'Possible Process Opening',
];
export type CharacteristicsType = (typeof CHARACTERISTICS_TYPES)[number];

export const MARKET_TYPES = ['Uruguay', 'LATAM', 'Rest of the world'];
export type MarketType = (typeof MARKET_TYPES)[number];

export const MODALITY_TYPES = [
  'Employee',
  'Independent (SAS UY)',
  'Independent (professional UY)',
  'Contractor',
];
export type ModalityType = (typeof MODALITY_TYPES)[number];

export const ENGLISH_LEVELS = [
  'A1 - Basic 1',
  'A1.2 - Basic 2',
  'A2 - Pre Intermediate',
  'B1 - Intermediate',
  'B2 - Upper Intermediate',
  'C1 - Advanced',
  'C2 - Advanced',
  'Native Speaker',
  'Pending evaluation',
];

export const TEST_TYPES = ['Coderbyte', 'Other'];

export const CODERBYTE_STATUS = [
  'Sent',
  'Completed',
  'Pending',
  'Resent (initial deadline expired)',
  'Deadline expired',
];

export const SENIORITY_OPTIONS: Record<string, string> = {
  advanced: 'Advanced',
  advanced_plus: 'Advanced ++',
  junior: 'Jr.',
  senior: 'Sr.',
};

export const PROPOSAL_RESPONSE = ['Proposal accepted', 'Proposal rejected', 'Evaluating proposal'];

export const COUNT_ROWS_PER_PAGE = 20;

export enum DocumentType {
  Common = 1,
  EnglishDocument = 2,
}